// =========================================================================
// Edge Interaction (W4)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   §「画面フロー」 辺タップ → Preview Highlight + Preview Panel → 「決定」
//   §11.2 取消可能タイミング: 確認モーダル前は別 edge タップで選択変更可
//   §「線質差カタログ」 タップ可能ハイライト / 選択中 (--accent-strong)
//
// 役割:
//   - W3 が描く canvas (`renderBoard`) の `onEvent` listener として動作
//   - 「どの辺が選択中か」 を保持し、選択変更を event として外部に発火する
//   - controller を直接呼ばない (controller は onEvent で listen して
//     confirm-modal を出す / preview-bar を更新する)
//
// 注意:
//   - W3 の canvas.ts は本コミット時点では未実装。型 `CanvasHandle` /
//     `CanvasEvent` は W3 担当ファイルから import する設計だが、未確定なため
//     当ファイル内で local type alias として最小限定義した。W3 が同名で
//     export し始めたら import 元を W3 ファイルに差し替える (INTERFACE_REQUESTS
//     にも記載)。
//   - タップ領域 >= 48dp は W3 側 (canvas.ts のヒット判定) で担保される前提。
//     ここでは「canvas が edgeTap event を発火した」 ことを信頼する。
// =========================================================================

// -------------------------------------------------------------------------
// W3 canvas との contract
// -------------------------------------------------------------------------
// W3 (`game/canvas.ts`) が提供する型を直接 import する。
// W3 の `CanvasEvent` は { type: 'edgeTap'|'nodeTap' } の2種類のみで
// `backgroundTap` は存在しない。背景 (空白部) タップによる選択解除は MVP では
// サポートせず、controller 側で「別 edge タップ → 選択切替」 / 「同 edge
// タップ → 何もしない (confirm-modal が前面)」 / 「modal の cancel/overlay
// タップ → clearSelection」 でカバーする。
// nodeTap は MVP では選択挙動に使わない (= 無視) が、将来 「ノード→隣接辺
// プリセレクト」 等の拡張を入れるとき同じ handle で listen できる。
// -------------------------------------------------------------------------

import type { CanvasEvent, CanvasHandle } from './canvas.js';
export type { CanvasEvent, CanvasHandle };

// -------------------------------------------------------------------------
// 公開 API
// -------------------------------------------------------------------------

/** W4 が外部 (= controller) に通知するイベント */
export type EdgeInteractionEvent =
  | { readonly type: 'edgeSelected'; readonly edgeId: number }
  | { readonly type: 'edgeDeselected' };

export interface EdgeInteractionHandle {
  /** W3 canvas にアタッチ。canvas が再生成されたら再 attach する */
  attach(canvas: CanvasHandle): void;
  /** attach を解除 (canvas 破棄前 / ページ離脱) */
  detach(): void;
  /** イベントリスナー登録 (複数可)。controller が confirm-modal を起こす */
  onEvent(listener: (event: EdgeInteractionEvent) => void): void;
  /** 登録解除 */
  offEvent(listener: (event: EdgeInteractionEvent) => void): void;
  /** 現在選択中の edgeId (UI 復元用)。未選択は null */
  readonly currentSelection: number | null;
  /**
   * 外部からの強制解除 (controller が確定 → applyOperation 後にクリア、
   * または勝敗到達時に呼ぶ)。listener には edgeDeselected が通知される。
   */
  clearSelection(): void;
}

/**
 * EdgeInteraction を生成する。
 *
 * 動作:
 *   - canvas.onEvent('edgeTap', edgeId) を受け取る
 *     - currentSelection === null      → 'edgeSelected'
 *     - currentSelection === edgeId    → 同じ辺を再タップ: 何もしない
 *                                        (controller が confirm-modal を出してる想定)
 *     - currentSelection !== edgeId    → 別 edge へ切り替え: 'edgeSelected' (新 edgeId)
 *       ※ spec §11.2: 確認モーダル前は別 edge タップで選択変更可
 *   - canvas.onEvent('backgroundTap') を受け取る
 *     - 選択中なら 'edgeDeselected' を発火
 *
 * controller は本 handle の onEvent に listen して、currentSelection が変わるたびに
 *   - preview-bar を更新
 *   - confirm-modal を show
 * を行う。
 */
export function createEdgeInteraction(): EdgeInteractionHandle {
  let attachedCanvas: CanvasHandle | null = null;
  let currentEdgeId: number | null = null;
  const listeners = new Set<(event: EdgeInteractionEvent) => void>();

  const emit = (event: EdgeInteractionEvent) => {
    // listener の発火中に listener 集合が変わっても安全になるよう snapshot
    for (const listener of Array.from(listeners)) {
      try {
        listener(event);
      } catch (err) {
        // listener の例外は隔離 (他の listener が止まらないようにする)
        // eslint-disable-next-line no-console
        console.error('[edge-interaction] listener threw', err);
      }
    }
  };

  const onCanvasEvent = (event: CanvasEvent) => {
    if (event.type === 'edgeTap') {
      const tapped = event.edgeId;
      if (currentEdgeId === tapped) {
        // 同じ edge を再タップ: 状態変化なし (modal は controller が制御)
        return;
      }
      currentEdgeId = tapped;
      emit({ type: 'edgeSelected', edgeId: tapped });
      return;
    }
    // 'nodeTap' は MVP では選択挙動に使わない (将来拡張余地)
  };

  const handle: EdgeInteractionHandle = {
    attach(canvas) {
      // W3 CanvasHandle は offEvent を持たないため、attach を多重に呼んでも
      // 旧 listener を canvas 側で外せない。controller は同じ EdgeInteraction
      // インスタンスを使い続け、canvas を作り直す時は新 canvas にのみ attach
      // することが期待される (canvas の destroy で listener も解放される)。
      attachedCanvas = canvas;
      canvas.onEvent(onCanvasEvent);
    },
    detach() {
      // W3 CanvasHandle に offEvent が無いため、ここでは参照を切るだけ。
      // canvas 側を destroy() する責任は controller。
      attachedCanvas = null;
    },
    onEvent(listener) {
      listeners.add(listener);
    },
    offEvent(listener) {
      listeners.delete(listener);
    },
    get currentSelection() {
      return currentEdgeId;
    },
    clearSelection() {
      if (currentEdgeId === null) return;
      currentEdgeId = null;
      emit({ type: 'edgeDeselected' });
    },
  };
  return handle;
}
